/**
 * SearchableDropdown Component
 *
 * @example
 * ```html
 * <select data-searchable="true" data-placeholder="Search providers...">
 *   <option value="1">University A</option>
 *   <option value="2">University B</option>
 * </select>
 * ```
 */

import '../components/searchable_dropdown.scss'

interface SearchableDropdownOptions {
  placeholder?: string
  noResultsText?: string
}

interface DropdownOption {
  value: string
  text: string
  selected: boolean
}

class SearchableDropdown {
  private selectElement: HTMLSelectElement
  // Assigned unconditionally by createWrapperAndInput() / createDropdownMenu(),
  // both called from init() in the constructor. tsc cannot trace assignment
  // through a helper call, hence the definite assignment assertions.
  private wrapper!: HTMLElement
  private inputContainer!: HTMLElement
  private searchInput!: HTMLInputElement
  private clearButton!: HTMLElement
  private dropdownMenu!: HTMLElement
  private options: SearchableDropdownOptions
  private isOpen: boolean = false
  private originalOptions: DropdownOption[] = []
  private mutationObserver?: MutationObserver
  private selectedText: string = '' // Track selected option text to distinguish display vs search mode

  // Navigation constants
  private readonly DIRECTION_DOWN = 1
  private readonly DIRECTION_UP = -1
  // Class selector for visible options
  private readonly VISIBLE_OPTION_SELECTOR =
    '.dropdown-item:not(.d-none):not(.disabled)'

  constructor(
    selectElement: HTMLSelectElement,
    options: SearchableDropdownOptions = {},
  ) {
    this.selectElement = selectElement
    this.options = {
      placeholder:
        options.placeholder ||
        `Search ${selectElement.title?.toLowerCase() || 'options'}...`,
      noResultsText: options.noResultsText || 'No results found',
    }

    this.init()
  }

  private init(): void {
    this.storeOriginalOptions()
    this.createWrapperAndInput()
    this.createDropdownMenu()
    this.selectElement.style.display = 'none'
    this.addEventListeners()

    this.renderOptions()
    this.updateSearchInputFromSelect()
    this.updateClearButtonVisibility()
  }

  private storeOriginalOptions(): void {
    this.originalOptions = Array.from(this.selectElement.options).map(
      (option) => ({
        value: option.value,
        text: option.text,
        selected: option.selected || option.value === this.selectElement.value,
      }),
    )
  }

  private createWrapperAndInput(): void {
    this.createClearButton()
    this.createSearchInput()

    const inputGroup = this.selectElement.closest('.input-group')

    if (inputGroup) {
      this.setupInputGroupLayout(inputGroup)
    } else {
      this.setupStandardLayout()
    }
  }

  private createSearchInput(): void {
    this.searchInput = document.createElement('input')
    this.searchInput.type = 'text'
    this.searchInput.className = `${this.selectElement.className} form-control`
    this.searchInput.placeholder = this.options.placeholder!
    this.searchInput.disabled = this.selectElement.disabled

    this.searchInput.setAttribute(
      'aria-label',
      this.selectElement.getAttribute('aria-label') || '',
    )
    this.searchInput.setAttribute('autocomplete', 'off')
    this.searchInput.setAttribute('spellcheck', 'false')

    if (this.selectElement.id) {
      this.searchInput.id = `${this.selectElement.id}_search`
    }
  }

  private setupInputGroupLayout(inputGroup: Element): void {
    this.wrapper = document.createElement('div')
    this.wrapper.className = 'searchable-dropdown'
    inputGroup.parentNode?.insertBefore(this.wrapper, inputGroup)
    this.wrapper.appendChild(inputGroup)
    this.wrapper.appendChild(this.selectElement)

    // Create a container for input + clear button + dropdown
    const inputContainer = document.createElement('div')
    inputContainer.className = 'position-relative'
    inputContainer.appendChild(this.searchInput)
    inputContainer.appendChild(this.clearButton)
    inputGroup.appendChild(inputContainer)

    this.inputContainer = inputContainer
  }

  private setupStandardLayout(): void {
    this.wrapper = document.createElement('div')
    this.wrapper.className = 'searchable-dropdown'
    this.selectElement.parentNode?.insertBefore(
      this.wrapper,
      this.selectElement,
    )
    this.wrapper.appendChild(this.selectElement)

    // Create a container for input + clear button + dropdown
    this.inputContainer = document.createElement('div')
    this.inputContainer.className = 'position-relative'
    this.inputContainer.appendChild(this.searchInput)
    this.inputContainer.appendChild(this.clearButton)
    this.wrapper.appendChild(this.inputContainer)
  }

  private createClearButton(): void {
    this.clearButton = document.createElement('button')
    this.clearButton.setAttribute('type', 'button')
    this.clearButton.className =
      'searchable-dropdown-clear btn-close position-absolute top-50 translate-middle-y d-none z-2'
    this.clearButton.setAttribute('aria-label', 'Clear selection')
    this.clearButton.setAttribute('tabindex', '-1') // Initially hidden, so not tabbable

    this.clearButton.addEventListener('click', (e) => {
      e.preventDefault()
      e.stopPropagation()
      this.clearSelection()
    })
  }

  private createDropdownMenu(): void {
    this.dropdownMenu = document.createElement('ul')
    this.dropdownMenu.className = 'dropdown-menu'
    this.inputContainer.appendChild(this.dropdownMenu)
  }

  private addEventListeners(): void {
    this.searchInput.addEventListener('focus', () => this.openDropdown())
    this.searchInput.addEventListener('input', (e) => {
      const inputValue = (e.target as HTMLInputElement).value
      this.handleSearch(inputValue)
      this.updateClearButtonVisibility()
    })
    this.searchInput.addEventListener('keydown', (e) => this.handleKeydown(e))

    document.addEventListener('click', (e) => {
      if (!this.wrapper.contains(e.target as Node)) {
        this.closeDropdown()
      }
    })

    this.selectElement.form?.addEventListener('reset', () => {
      setTimeout(() => this.updateSearchInputFromSelect(), 0)
    })

    this.mutationObserver = new MutationObserver(() => {
      this.syncWithSelectElement()
    })
    this.mutationObserver.observe(this.selectElement, {
      attributes: true,
      attributeFilter: ['value'],
      childList: true,
    })
  }

  private openDropdown(): void {
    this.isOpen = true
    this.dropdownMenu.classList.add('show')

    // If input value matches the selected text, show all options (display mode)
    // Otherwise, filter by the current query (search mode)
    const currentValue = this.searchInput.value
    const shouldShowAll =
      this.selectedText && currentValue === this.selectedText

    if (shouldShowAll) {
      this.renderOptions('')
    } else {
      this.renderOptions(currentValue.trim().toLowerCase())
    }
  }

  private closeDropdown(): void {
    this.isOpen = false
    this.dropdownMenu.classList.remove('show')
  }

  private handleSearch(query: string): void {
    if (!this.isOpen) {
      this.openDropdown()
    }
    this.renderOptions(query.trim().toLowerCase())
  }

  private handleKeydown(e: KeyboardEvent): void {
    switch (e.key) {
      case 'Escape':
        this.closeDropdown()
        break
      case 'Tab':
        this.closeDropdown()
        break
      case 'ArrowDown':
        e.preventDefault()
        e.stopPropagation()
        if (!this.isOpen) {
          this.openDropdown()
        }
        this.navigateOptions(this.DIRECTION_DOWN)
        break
      case 'ArrowUp':
        e.preventDefault()
        e.stopPropagation()
        if (!this.isOpen) {
          this.openDropdown()
        }
        this.navigateOptions(this.DIRECTION_UP)
        break
      case 'Enter':
        e.preventDefault()
        e.stopPropagation()
        this.selectHighlighted()
    }
  }

  private navigateOptions(direction: number): void {
    if (!this.isOpen) {
      return
    }

    const visibleOptions = Array.from(
      this.dropdownMenu.querySelectorAll<HTMLElement>(
        this.VISIBLE_OPTION_SELECTOR,
      ),
    )

    if (visibleOptions.length === 0) {
      return
    }

    // Either start at the first option, or at the currently active option. Then wrap around as required.
    let nextIndex = 0

    const currentlyActive = visibleOptions.find((item) =>
      item.classList.contains('active'),
    )
    if (currentlyActive) {
      currentlyActive.classList.remove('active')
      nextIndex = visibleOptions.indexOf(currentlyActive)
      nextIndex = (nextIndex + direction) % visibleOptions.length
      nextIndex = (nextIndex + visibleOptions.length) % visibleOptions.length
    }

    visibleOptions[nextIndex].classList.add('active')
  }

  private selectHighlighted(): void {
    const activeItem = Array.from(
      this.dropdownMenu.querySelectorAll<HTMLElement>('.dropdown-item'),
    ).find((item) => item.classList.contains('active'))

    if (activeItem) {
      this.selectOption(activeItem.dataset.value!)
    } else {
      const firstOption = this.dropdownMenu.querySelector(
        this.VISIBLE_OPTION_SELECTOR,
      ) as HTMLElement
      if (firstOption) {
        this.selectOption(firstOption.dataset.value!)
      }
    }
  }

  private renderOptions(query: string = ''): void {
    this.dropdownMenu.innerHTML = ''

    const filteredOptions = this.originalOptions.filter((option) => {
      if (!option.value) {
        return query === ''
      }
      return (
        option.text.toLowerCase().includes(query) ||
        option.value.toLowerCase().includes(query)
      )
    })

    if (filteredOptions.length === 0) {
      const noResults = document.createElement('li')
      noResults.className = 'dropdown-item disabled'
      noResults.textContent = this.options.noResultsText!
      this.dropdownMenu.appendChild(noResults)
      return
    }

    filteredOptions.forEach((option) => {
      const optionElement = document.createElement('li')
      const linkElement = optionElement.appendChild(document.createElement('a'))
      linkElement.className = 'dropdown-item btn btn-link'
      linkElement.addEventListener('click', (_) => {
        this.handleOptionClick(option)
      })
      linkElement.dataset.value = option.value
      linkElement.textContent = !option.value ? 'All' : option.text

      if (option.selected || option.value === this.selectElement.value) {
        linkElement.classList.add('active')
      }

      this.dropdownMenu.appendChild(optionElement)
    })
  }

  private handleOptionClick(option: DropdownOption): void {
    this.clearActiveOption()
    this.selectOption(option.value)
  }

  private selectOption(value: string): void {
    this.selectElement.value = value
    this.originalOptions.forEach((option) => {
      option.selected = option.value === value
    })

    // Store the selected option's text so we can show all options when reopening
    const selectedOption = this.originalOptions.find(
      (option) => option.value === value,
    )
    this.selectedText = selectedOption?.text || ''

    this.updateSearchInputFromSelect()
    this.selectElement.dispatchEvent(new Event('change', { bubbles: true }))
    this.closeDropdown()
  }

  private updateSearchInputFromSelect(): void {
    const currentValue = this.selectElement.value
    const selectedOption = this.originalOptions.find(
      (option) => option.selected || option.value === currentValue,
    )

    if (!selectedOption || !currentValue) {
      this.searchInput.value = ''
      this.searchInput.placeholder = this.options.placeholder!
      this.selectedText = ''
    } else {
      this.searchInput.value = selectedOption.text
      this.selectedText = selectedOption.text
    }

    this.originalOptions.forEach((option) => {
      option.selected = option.value === currentValue
    })

    this.updateClearButtonVisibility()
  }

  public syncWithSelectElement(): void {
    this.storeOriginalOptions()
    this.updateSearchInputFromSelect()
  }

  private clearSelection(): void {
    this.selectElement.value = ''

    this.originalOptions.forEach((option) => {
      option.selected = option.value === ''
    })

    this.searchInput.value = ''
    this.selectedText = ''
    this.updateClearButtonVisibility()
    this.selectElement.dispatchEvent(new Event('change', { bubbles: true }))
    this.closeDropdown()
  }

  private clearActiveOption(): void {
    this.dropdownMenu.querySelectorAll('.dropdown-item').forEach((item) => {
      item.classList.remove('active')
    })
  }

  private updateClearButtonVisibility(): void {
    const hasValue =
      this.searchInput.value.trim() !== '' || this.selectElement.value !== ''

    if (hasValue) {
      this.clearButton.classList.remove('d-none')
      this.clearButton.classList.add('d-flex')
      this.clearButton.setAttribute('tabindex', '0')
    } else {
      this.clearButton.classList.remove('d-flex')
      this.clearButton.classList.add('d-none')
      this.clearButton.setAttribute('tabindex', '-1')
    }
  }

  public destroy(): void {
    this.mutationObserver?.disconnect()
  }
}

/**
 * Initializes all searchable dropdowns on the page.
 * Looks for select elements with the following attributes:
 * - data-searchable="true"
 * - data-placeholder [optional]
 * - data-no-results [optional]
 */
export function initSearchableDropdowns(): void {
  document
    .querySelectorAll('select[data-searchable="true"]')
    .forEach((select) => {
      const placeholder = select.getAttribute('data-placeholder') || undefined
      const noResultsText = select.getAttribute('data-no-results') || undefined

      new SearchableDropdown(select as HTMLSelectElement, {
        placeholder,
        noResultsText,
      })
    })
}
